import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { LikeButton } from './LikeButton';
import { CommentThread } from './CommentThread';
import {
  createImageComment,
  deleteImageComment,
  getImageDetail,
  getImageLikes,
  getImageShareUrl,
  listImageComments,
  setImageLike,
  setImageVisibility,
  type ImageDetail,
  type LikeState,
} from '../../services/communityService';
import { useTranslation } from '../../i18n/useTranslation';
import './ImageSocialPanel.css';

export const imageDetailQueryKey = (imageId: string) => ['imageDetail', imageId] as const;
const imageLikesQueryKey = (imageId: string) => ['imageLikes', imageId] as const;
const imageCommentsQueryKey = (imageId: string) => ['imageComments', imageId] as const;
const imageShareQueryKey = (imageId: string) => ['imageShare', imageId] as const;

export interface ImageSocialPanelProps {
  imageId: string;
  /** Heading level of the panel's sub-headings (default 3). */
  headingLevel?: 2 | 3;
}

/**
 * Sharing and reactions of one gallery image: the owner switches it between
 * private and public and copies the share link; on a public image everyone
 * signed in can like and comment. Used in the ImageViewer and on /images/:id.
 */
export function ImageSocialPanel({ imageId, headingLevel = 3 }: ImageSocialPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const detail = useQuery({ queryKey: imageDetailQueryKey(imageId), queryFn: () => getImageDetail(imageId) });
  const image = detail.data;
  const isPublic = image?.visibility === 'public';

  const likes = useQuery({
    queryKey: imageLikesQueryKey(imageId),
    queryFn: () => getImageLikes(imageId),
    enabled: isPublic,
  });
  const share = useQuery({
    queryKey: imageShareQueryKey(imageId),
    queryFn: () => getImageShareUrl(imageId),
    enabled: isPublic && image?.isOwner === true,
  });

  const visibilityMutation = useMutation({
    mutationFn: (visibility: 'public' | 'private') => setImageVisibility(imageId, visibility),
    onSuccess: (_data, visibility) => {
      setError(null);
      queryClient.setQueryData<ImageDetail>(imageDetailQueryKey(imageId), (prev) => (prev ? { ...prev, visibility } : prev));
      setAnnouncement(visibility === 'public' ? t('imageSocial.nowPublic') : t('imageSocial.nowPrivate'));
    },
    onError: () => setError(t('imageSocial.visibilityFailed')),
  });

  const likeMutation = useMutation({
    mutationFn: (liked: boolean) => setImageLike(imageId, liked),
    onSuccess: (state) => {
      setError(null);
      queryClient.setQueryData<LikeState>(imageLikesQueryKey(imageId), state);
    },
    onError: () => setError(t('community.likeFailed')),
  });

  async function copyShareLink(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setAnnouncement(t('imageSocial.copied'));
    } catch {
      setError(t('imageSocial.copyFailed'));
    }
  }

  if (detail.isLoading) {
    return (
      <LiveRegion>
        <Text color="muted">{t('imageSocial.loading')}</Text>
      </LiveRegion>
    );
  }
  if (detail.isError || !image) {
    return (
      <LoadErrorState
        title={t('imageSocial.loadError')}
        titleLevel={headingLevel === 2 ? 3 : 4}
        onRetry={() => void detail.refetch()}
        compact
      />
    );
  }

  return (
    <div className="rg-image-social" data-testid="image-social-panel">
      {image.isOwner && (
        <div className="rg-image-social__block">
          <Heading level={headingLevel} variant="subsection">{t('imageSocial.visibilityTitle')}</Heading>
          <div className="rg-image-social__toggle" role="group" aria-label={t('imageSocial.visibilityTitle')}>
            <Button
              type="button"
              size="sm"
              variant={isPublic ? 'ghost' : 'secondary'}
              aria-pressed={!isPublic}
              disabled={visibilityMutation.isPending}
              onClick={() => {
                if (isPublic) visibilityMutation.mutate('private');
              }}
            >
              <span aria-hidden="true">🔒 </span>{t('imageSocial.private')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={isPublic ? 'secondary' : 'ghost'}
              aria-pressed={isPublic}
              disabled={visibilityMutation.isPending}
              onClick={() => {
                if (!isPublic) visibilityMutation.mutate('public');
              }}
            >
              <span aria-hidden="true">🌍 </span>{t('imageSocial.public')}
            </Button>
          </div>
          <Text size="sm" color="secondary">
            {isPublic ? t('imageSocial.publicHint') : t('imageSocial.privateHint')}
          </Text>
          {isPublic && share.data && (
            <div className="rg-image-social__share">
              <Input
                label={t('imageSocial.shareLink')}
                value={share.data}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button type="button" size="sm" variant="secondary" onClick={() => void copyShareLink(share.data)}>
                {t('imageSocial.copyLink')}
              </Button>
            </div>
          )}
        </div>
      )}

      {error && (
        <Alert tone="danger" compact onDismiss={() => setError(null)} dismissLabel={t('comments.dismiss')}>
          {error}
        </Alert>
      )}

      {isPublic && (
        <div className="rg-image-social__block">
          {likes.data && (
            <LikeButton
              liked={likes.data.liked}
              count={likes.data.count}
              pending={likeMutation.isPending}
              onToggle={() => likeMutation.mutate(!likes.data.liked)}
            />
          )}
          <Heading level={headingLevel} variant="subsection">{t('comments.title')}</Heading>
          <CommentThread
            testId="image-comments"
            queryKey={imageCommentsQueryKey(imageId)}
            load={() => listImageComments(imageId)}
            create={(body) => createImageComment(imageId, body)}
            remove={(commentId) => deleteImageComment(imageId, commentId)}
          />
        </div>
      )}
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </div>
  );
}

export default ImageSocialPanel;
